/**
 * Size Calculation Utilities
 *
 * Pure functions for calculating size information of canvas objects.
 * No React dependencies - can be used in stores or components.
 *
 * @see hooks/useSizeIndicator.ts - Hook that uses these functions
 */

import type { FabricObject, FabricImage } from 'fabric';
import {
  getQualityInfo,
  convertFromMm,
  QUALITY_LABELS,
  type QualityInfo,
  type UnitSystem,
} from '@/config/printArea';

/**
 * Size information for the selected object
 */
export interface SizeInfo {
  /** Width in cm */
  width: number;
  /** Height in cm */
  height: number;
  /** Unit string, always 'cm' */
  unit: string;
  /** Quality info (only for images) */
  quality: QualityInfo | null;
  /** Formatted display string */
  displaySize: string;
  /** Formatted quality label */
  qualityLabel: string | null;
}

/**
 * Calculate DPI for an image object
 *
 * @param obj - The Fabric.js image object
 * @param physicalWidthMm - The physical width in millimeters
 * @returns The calculated DPI value
 */
export function calculateImageDPI(
  obj: FabricImage,
  physicalWidthMm: number
): number {
  // Get original image dimensions
  const originalWidth = obj.width || 0;

  // The original pixels are stretched/compressed to the physical size
  // DPI = original pixels / physical inches
  const physicalWidthInch = physicalWidthMm / 25.4;

  if (physicalWidthInch <= 0) return 0;

  return Math.round(originalWidth / physicalWidthInch);
}

/**
 * Calculate size info for a Fabric.js object
 *
 * @param obj - The Fabric.js object to calculate size for
 * @param pxPerMm - The pixels per millimeter ratio
 * @param locale - The locale for the quality label (default: 'en')
 * @returns SizeInfo object or null if object is invalid
 */
export function calculateSizeInfo(
  obj: FabricObject | null | undefined,
  pxPerMm: number,
  locale: string = 'en'
): SizeInfo | null {
  if (!obj) return null;

  // Always centimetres: every size input, the print-area tooltip and the
  // guided slider read and write cm. Following the browser locale showed
  // inches under a "cm" label for en-US/en-GB and turned a typed width into
  // the wrong size.
  const unitSystem: UnitSystem = 'metric';

  // Get scaled dimensions in pixels
  const scaledWidthPx = obj.getScaledWidth();
  const scaledHeightPx = obj.getScaledHeight();

  // Convert to mm using the pxPerMm ratio
  const widthMm = scaledWidthPx / pxPerMm;
  const heightMm = scaledHeightPx / pxPerMm;

  // Convert to display units (cm)
  const widthConverted = convertFromMm(widthMm, unitSystem);
  const heightConverted = convertFromMm(heightMm, unitSystem);

  // Calculate quality (only for images)
  let quality: QualityInfo | null = null;
  let qualityLabel: string | null = null;

  if (obj.type === 'image') {
    const dpi = calculateImageDPI(obj as FabricImage, widthMm);
    quality = getQualityInfo(dpi);
    qualityLabel =
      QUALITY_LABELS[quality.level][locale.startsWith('de') ? 'de' : 'en'];
  }

  // Format display string
  const displaySize = `${widthConverted.value.toFixed(2)} × ${heightConverted.value.toFixed(2)} ${widthConverted.unit}`;

  return {
    width: widthConverted.value,
    height: heightConverted.value,
    unit: widthConverted.unit,
    quality,
    displaySize,
    qualityLabel,
  };
}

/**
 * @deprecated Use PrintContext.pxPerMm from types/printContext.ts instead.
 * The pxPerMm calculation is now handled by createPrintContext() in the
 * productSelectionStore, ensuring a Single Source of Truth.
 *
 * @see types/printContext.ts - createPrintContext()
 * @see stores/productSelectionStore.ts - activePrintContext
 */
